/**
 * Helpers d'auth client — source de vérité = `localStorage.alterrego_user`,
 * peuplé par CompteScreen (sign-in Apple) et refreshé via GET /users/me.
 *
 * Pourquoi `isAdmin` est lu ici et pas dérivé de l'email :
 *   - Backend auto-promote sur la liste ALTERREGO_ADMIN_EMAILS *à partir de
 *     l'email retourné par l'idToken Apple*. Si l'utilisateur a choisi "Hide
 *     My Email", l'email stocké côté backend ET côté client est l'adresse
 *     `xxxx@privaterelay.appleid.com`, qui n'est pas dans la liste admin.
 *   - Le backend peut quand même flagger admin via d'autres signaux (lien
 *     mySafe, flag manuel en DB) qui ne sont pas observables côté client à
 *     partir de l'email seul.
 *   - Source de vérité = `is_admin` en DB → exposé par /auth/apple/exchange
 *     et /users/me sous le champ `isAdmin` du payload `user`.
 */
import { useEffect, useState } from 'react';
import { apiFetch, apiUrl } from './api';

export interface StoredUser {
  email: string;
  name: string;
  isAdmin?: boolean;
}

/**
 * Event global émis quand le backend renvoie `401 Unauthorized` : la session est
 * considérée expirée. La bannière `SessionExpiredBanner` (montée à la racine)
 * l'écoute pour proposer reconnexion / mise à jour de l'app.
 */
export const SESSION_EXPIRED_EVENT = 'session-expired';
/** Event global émis après une reconnexion réussie (réarme la bannière). */
export const SESSION_RESTORED_EVENT = 'session-restored';

/** JWT courant (`alterrego_jwt`) ou `null`. Source de vérité pour `Authorization`. */
export function getAuthToken(): string | null {
  return localStorage.getItem('alterrego_jwt');
}

/**
 * Session expirée : purge le JWT et notifie l'app (bannière globale). Idempotent,
 * aucun retry — l'appelant reçoit la réponse 401 telle quelle. À appeler sur tout
 * 401 backend (voir `authFetch` / `apiFetch`).
 */
export function handleUnauthorized(): void {
  localStorage.removeItem('alterrego_jwt');
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

/** À appeler après un re-login réussi pour masquer la bannière de session expirée. */
export function notifySessionRestored(): void {
  window.dispatchEvent(new CustomEvent(SESSION_RESTORED_EVENT));
}

/**
 * @deprecated Utiliser `apiFetch(path, { auth: true })` (utils/api), qui émet
 * aussi `X-Alterrego-Tenant`. Alias conservé pour compat pendant la migration A5 :
 * accepte une URL absolue ou un path relatif, force l'auth, gère le 401 global.
 */
export function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  return apiFetch(typeof input === 'string' ? input : String(input), { ...init, auth: true });
}

export function getCurrentUser(): StoredUser | null {
  try {
    const raw = localStorage.getItem('alterrego_user');
    if (!raw) return null;
    return JSON.parse(raw) as StoredUser;
  } catch {
    return null;
  }
}

export function isCurrentUserAdmin(): boolean {
  return getCurrentUser()?.isAdmin === true;
}

/** Une session est ouverte (JWT présent). Ne dit rien du rôle. */
export function isAuthenticated(): boolean {
  return !!getAuthToken();
}

/**
 * Version réactive de `isCurrentUserAdmin`, pour les composants montés en
 * permanence (TabBar, garde de route) qui doivent suivre un login / logout
 * survenu ailleurs dans l'app.
 *
 * Sondage 1 s plutôt qu'un event : `localStorage` n'émet `storage` que pour les
 * AUTRES onglets, jamais pour celui qui écrit — or ici l'écriture (CompteScreen)
 * et la lecture sont dans la même page. Même cadence et même raison que le
 * `setInterval` déjà en place dans l'avatar de la TabBar.
 *
 * Lecture synchrone au premier rendu : au démarrage à froid `localStorage` est
 * déjà peuplé, donc pas de clignotement « staff → admin ».
 */
export function useAdminState(): { admin: boolean; authed: boolean } {
  const [state, setState] = useState(() => ({
    admin: isCurrentUserAdmin(),
    authed: isAuthenticated(),
  }));

  useEffect(() => {
    const id = setInterval(() => {
      const next = { admin: isCurrentUserAdmin(), authed: isAuthenticated() };
      setState((prev) =>
        prev.admin === next.admin && prev.authed === next.authed ? prev : next,
      );
    }, 1000);
    return () => clearInterval(id);
  }, []);

  return state;
}

/**
 * Refresh asynchrone depuis /users/me. Utilise le JWT en localStorage.
 * Met à jour `alterrego_user.isAdmin` (+ avatar/name si retournés).
 * Best-effort : silencieux en cas d'échec réseau / 401 (la session reste OK).
 */
export async function refreshCurrentUser(): Promise<StoredUser | null> {
  const jwt = localStorage.getItem('alterrego_jwt');
  if (!jwt) return null;
  try {
    // Raw fetch volontaire (pas apiFetch) : best-effort, un 401 ici ne doit PAS
    // déclencher la bannière de session expirée (refresh de fond au boot).
    const res = await fetch(apiUrl('/users/me'), {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    if (!res.ok) return null;
    const me = await res.json() as { email?: string; displayName?: string; isAdmin?: boolean };
    const prev = getCurrentUser();
    const updated: StoredUser = {
      email: me.email ?? prev?.email ?? '',
      name: me.displayName ?? prev?.name ?? '',
      isAdmin: me.isAdmin === true,
    };
    localStorage.setItem('alterrego_user', JSON.stringify(updated));
    return updated;
  } catch {
    return null;
  }
}
